import { useCallback, useEffect, useMemo, useState } from "react";
import { useEmployerActionAdapter, useEmployerMode } from "../components/employer/EmployerModeContext";
import {
  createEmployerTrustIncident,
  getEmployerProfile,
  runEmployerTrustRealityCheck,
  saveEmployerProfile,
  updateEmployerTrustIncident,
  updateEmployerTrustReviewArea,
  updateEmployerTrustReviewTask,
} from "../services/employer/employerWorkspaceAdapters";

function normalizeRows(rows) {
  return Array.isArray(rows) ? rows.filter(Boolean) : [];
}

export default function useEmployerOsWorkspace({ preloadDetails = false, preloadFiling = false } = {}) {
  const adapter = useEmployerActionAdapter();
  const { isTraining, currentRun } = useEmployerMode();
  const [ads, setAds] = useState([]);
  const [selectedAdId, setSelectedAdId] = useState("");
  const [applications, setApplications] = useState([]);
  const [detailsById, setDetailsById] = useState({});
  const [filingById, setFilingById] = useState({});
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState({
    ads: false,
    applications: false,
    profile: false,
  });
  const [error, setError] = useState("");

  const selectedAd = useMemo(
    () => normalizeRows(ads).find((entry) => String(entry.id) === String(selectedAdId)) || null,
    [ads, selectedAdId]
  );

  const loadAds = useCallback(async () => {
    try {
      setLoading((current) => ({ ...current, ads: true }));
      const result = await adapter.loadAds({ page: 1, limit: 100 });
      const nextAds = normalizeRows(result?.rows);
      setAds(nextAds);
      setError("");
      return nextAds;
    } catch (nextError) {
      setError(nextError.response?.data?.message || "Anzeigen konnten nicht geladen werden.");
      return [];
    } finally {
      setLoading((current) => ({ ...current, ads: false }));
    }
  }, [adapter]);

  const loadProfile = useCallback(async () => {
    try {
      setLoading((current) => ({ ...current, profile: true }));
      const nextProfile = await getEmployerProfile();
      setProfile(nextProfile);
      return nextProfile;
    } catch (nextError) {
      setError(nextError.response?.data?.message || "Arbeitgeberprofil konnte nicht geladen werden.");
      return null;
    } finally {
      setLoading((current) => ({ ...current, profile: false }));
    }
  }, []);

  const persistProfile = useCallback(async (payload) => {
    try {
      setLoading((current) => ({ ...current, profile: true }));
      const nextProfile = await saveEmployerProfile(payload);
      setProfile(nextProfile);
      setError("");
      return nextProfile;
    } catch (nextError) {
      setError(nextError.response?.data?.message || "Arbeitgeberprofil konnte nicht gespeichert werden.");
      throw nextError;
    } finally {
      setLoading((current) => ({ ...current, profile: false }));
    }
  }, []);

  const persistTrustReviewArea = useCallback(async (areaKey, payload) => {
    try {
      setLoading((current) => ({ ...current, profile: true }));
      const nextProfile = await updateEmployerTrustReviewArea(areaKey, payload);
      setProfile(nextProfile);
      setError("");
      return nextProfile;
    } catch (nextError) {
      setError(nextError.response?.data?.message || "Trust review area konnte nicht gespeichert werden.");
      throw nextError;
    } finally {
      setLoading((current) => ({ ...current, profile: false }));
    }
  }, []);

  const persistTrustReviewTask = useCallback(async (taskId, payload) => {
    try {
      setLoading((current) => ({ ...current, profile: true }));
      const nextProfile = await updateEmployerTrustReviewTask(taskId, payload);
      setProfile(nextProfile);
      setError("");
      return nextProfile;
    } catch (nextError) {
      setError(nextError.response?.data?.message || "Trust review task konnte nicht gespeichert werden.");
      throw nextError;
    } finally {
      setLoading((current) => ({ ...current, profile: false }));
    }
  }, []);

  const persistTrustIncident = useCallback(async (incidentId, payload) => {
    try {
      setLoading((current) => ({ ...current, profile: true }));
      const nextProfile = incidentId
        ? await updateEmployerTrustIncident(incidentId, payload)
        : await createEmployerTrustIncident(payload);
      setProfile(nextProfile);
      setError("");
      return nextProfile;
    } catch (nextError) {
      setError(nextError.response?.data?.message || "Trust incident konnte nicht gespeichert werden.");
      throw nextError;
    } finally {
      setLoading((current) => ({ ...current, profile: false }));
    }
  }, []);

  const persistTrustRealityCheck = useCallback(async (payload) => {
    try {
      setLoading((current) => ({ ...current, profile: true }));
      const nextProfile = await runEmployerTrustRealityCheck(payload);
      setProfile(nextProfile);
      setError("");
      return nextProfile;
    } catch (nextError) {
      setError(nextError.response?.data?.message || "Employer Reality Check konnte nicht ausgefuehrt werden.");
      throw nextError;
    } finally {
      setLoading((current) => ({ ...current, profile: false }));
    }
  }, []);

  const loadApplications = useCallback(async (adId) => {
    if (!adId) {
      setApplications([]);
      setDetailsById({});
      setFilingById({});
      return [];
    }

    try {
      setLoading((current) => ({ ...current, applications: true }));
      const result = await adapter.loadApplications({ adId });
      const nextApplications = normalizeRows(result?.rows);
      setApplications(nextApplications);
      setDetailsById({});
      setFilingById({});
      setError("");
      return nextApplications;
    } catch (nextError) {
      setApplications([]);
      setDetailsById({});
      setFilingById({});
      setError(nextError.response?.data?.message || "Kandidaten konnten nicht geladen werden.");
      return [];
    } finally {
      setLoading((current) => ({ ...current, applications: false }));
    }
  }, [adapter]);

  const ensureDecisionSummary = useCallback(async (applicationId) => {
    const key = String(applicationId);
    if (detailsById[key]) return detailsById[key];
    const detail = await adapter.loadDecisionSummary({ applicationId });
    setDetailsById((current) => ({ ...current, [key]: detail || null }));
    return detail || null;
  }, [adapter, detailsById]);

  const ensureFilingKit = useCallback(async (applicationId) => {
    const key = String(applicationId);
    if (filingById[key]) return filingById[key];
    const filing = await adapter.loadAuthorityFilingKit({ applicationId });
    const normalized = filing?.kit || filing || null;
    setFilingById((current) => ({ ...current, [key]: normalized }));
    return normalized;
  }, [adapter, filingById]);

  const refreshWorkspace = useCallback(async () => {
    const nextAds = await loadAds();
    await loadProfile();
    const adId = selectedAdId || (isTraining && currentRun?.job_ad_snapshot?.id ? String(currentRun.job_ad_snapshot.id) : String(nextAds[0]?.id || ""));
    if (adId) {
      setSelectedAdId(String(adId));
      await loadApplications(adId);
    }
  }, [currentRun, isTraining, loadAds, loadApplications, loadProfile, selectedAdId]);

  useEffect(() => {
    loadAds().then((nextAds) => {
      const nextAdId = isTraining && currentRun?.job_ad_snapshot?.id
        ? String(currentRun.job_ad_snapshot.id)
        : String(nextAds[0]?.id || "");
      if (nextAdId) setSelectedAdId((current) => current || nextAdId);
    });
    loadProfile();
  }, [currentRun, isTraining, loadAds, loadProfile]);

  useEffect(() => {
    if (isTraining && currentRun?.job_ad_snapshot?.id) {
      setSelectedAdId(String(currentRun.job_ad_snapshot.id));
    }
  }, [currentRun, isTraining]);

  useEffect(() => {
    if (!selectedAdId) return;
    loadApplications(selectedAdId);
  }, [loadApplications, selectedAdId]);

  useEffect(() => {
    if (!applications.length) return;
    if (preloadDetails) {
      Promise.all(applications.map((entry) => ensureDecisionSummary(entry.id))).catch(() => {});
    }
    if (preloadFiling) {
      Promise.all(applications.map((entry) => ensureFilingKit(entry.id))).catch(() => {});
    }
  }, [applications, ensureDecisionSummary, ensureFilingKit, preloadDetails, preloadFiling]);

  return {
    adapter,
    ads,
    applications,
    detailsById,
    filingById,
    profile,
    selectedAd,
    selectedAdId,
    setSelectedAdId,
    loading,
    error,
    isTraining,
    currentRun,
    loadAds,
    loadProfile,
    persistProfile,
    persistTrustReviewArea,
    persistTrustReviewTask,
    persistTrustIncident,
    persistTrustRealityCheck,
    loadApplications,
    ensureDecisionSummary,
    ensureFilingKit,
    refreshWorkspace,
  };
}
